/* Sitewide one-by-one reveal for repeated items (tiles, cards, columns…).
   Loaded on both the Orizza (layout/theme.liquid) and Frost
   (layout/frost-theme.liquid) layouts; styles in assets/reveal-stagger.css.

   No per-section markup needed: inside every top-level section it looks for
   "item groups" — an element whose children are 2+ look-alike, reasonably
   sized boxes (same tag + same first class) — and fades/slides each item up
   in turn as it scrolls into view, so a row of 4 tiles plays tile 1, tile 2,
   tile 3, tile 4. Items scrolled into view later (e.g. via a rail's arrows)
   reveal on their own when they arrive. It replays every time: once an item
   has fully left the screen it's reset, so coming back plays it again.

   Sections with no repeated grid can opt parts in with data-rs-step (they
   then reveal one after another in page order, like tiles).

   Opt out of a group with data-rs-skip on it (or any ancestor). Lists that
   already run their own staggered reveal (Shop by Category, Curated
   Collection) are skipped so the two don't fight over the same tiles. */
(function () {
  var STEP_MS = 80; // gap between consecutive items
  var MAX_DELAY_MS = 480; // cap so long rows don't wait forever
  var DURATION_MS = 450;
  var MIN_ITEM_SIZE = 40; // px; ignores dots, icons, tiny buttons

  var SKIP_SELECTOR = [
    '[data-rs-skip]',
    '[data-orizza-reveal-list]',
    '[data-orizza-cc-reveal-list]',
    '.orizza-hero',
    '.frost-hero',
    '[class*="marquee"]',
    '[role="tablist"]',
    'form',
    'select',
    'svg'
  ].join(',');

  function signature(el) {
    return el.tagName + '.' + (el.classList[0] || '');
  }

  // A section script that runs its own reveal marks its elements with a
  // data-*reveal* attribute (data-orizza-reveal-list, data-orizza-sig-reveal,
  // data-orizza-her-reveal…). Anything inside one of those is left alone so
  // the two scripts never animate the same element.
  function hasOwnReveal(el, stop) {
    for (var node = el; node && node !== stop; node = node.parentElement) {
      var names = node.getAttributeNames();
      for (var i = 0; i < names.length; i++) {
        if (names[i].indexOf('reveal') !== -1) return true;
      }
    }
    return false;
  }

  function isItemGroup(el) {
    if (el.children.length < 2 || el.children.length > 60) return null;

    // Only real boxes count — thin dividers, dots and hidden duplicates
    // between the items are ignored rather than disqualifying the group.
    var sized = Array.prototype.filter.call(el.children, function (c) {
      return !/^(SCRIPT|STYLE|TEMPLATE|LINK|META|NOSCRIPT)$/.test(c.tagName) && isSized(c);
    });
    if (sized.length < 2) return null;

    var sig = signature(sized[0]);
    for (var i = 1; i < sized.length; i++) {
      if (signature(sized[i]) !== sig) return null;
    }

    // Stacked slides (absolutely positioned on top of each other) are driven
    // by their own slider scripts — leave them alone.
    var pos = getComputedStyle(sized[0]).position;
    if (pos === 'absolute' || pos === 'fixed') return null;
    // Same for items stacked in one spot another way (e.g. Heritage's stage
    // texts: grid-area 1/1, shown one at a time by a timer, the hidden ones
    // nudged a few px) — revealing them would flash them all on top of each
    // other. Treat any real overlap between neighbours as "stacked".
    var a = sized[0].getBoundingClientRect();
    var b = sized[1].getBoundingClientRect();
    var overlapW = Math.min(a.right, b.right) - Math.max(a.left, b.left);
    var overlapH = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
    if (overlapW > 0 && overlapH > 0 && overlapW * overlapH > 0.25 * Math.min(a.width * a.height, b.width * b.height)) {
      return null;
    }
    // Items another script keeps invisible on purpose (inactive states) —
    // but not the ones only hidden by Frost's pre-reveal section fade.
    if (sized.some(function (c) {
      return getComputedStyle(c).opacity === '0' && !c.matches('.frost-reveal > * > *');
    })) return null;

    // Skip items another script already animates via inline opacity.
    if (sized.some(function (c) { return c.style.opacity !== ''; })) return null;

    return sized;
  }

  function isSized(el) {
    return el.offsetWidth >= MIN_ITEM_SIZE && el.offsetHeight >= MIN_ITEM_SIZE;
  }

  // One section's scan as a list of tiny steps (one per element) so
  // runChunked can split even a big section (e.g. a 24-card tabbed rail)
  // across several short tasks. Each step also does that element's rail lock.
  function sectionSteps(section, animate, allItems) {
    var els = section.querySelectorAll('*');
    var items = [];
    var groups = [];
    var steps = [function () {
      if (!animate) return;
      // Explicit steps: sections without a repeated grid (e.g. Complete the
      // Look — one image, one product at a time) mark the parts that should
      // come in one after another with data-rs-step. Hidden ones (inactive
      // slides) are left untouched so they're never stuck invisible.
      items = Array.prototype.filter.call(section.querySelectorAll('[data-rs-step]'), function (el) {
        return isSized(el) && !el.closest(SKIP_SELECTOR);
      });
      groups = items.slice();
    }];
    Array.prototype.forEach.call(els, function (el) {
      steps.push(function () {
        lockRail(el);
        if (!animate) return;
        if (el.closest(SKIP_SELECTOR) || el.hasAttribute('data-rs-step')) return;
        if (hasOwnReveal(el, section)) return;
        // Outermost group wins: don't also stagger the insides of an item.
        for (var i = 0; i < groups.length; i++) {
          if (groups[i] !== el && groups[i].contains(el)) return;
        }
        var groupItems = isItemGroup(el);
        if (!groupItems) return;
        groups.push(el);
        items.push.apply(items, groupItems);
      });
    });
    steps.push(function () { allItems.push.apply(allItems, items); });
    return steps;
  }

  function finish(item) {
    // Hand the element back to its own CSS (hover transforms, transitions…)
    item.classList.remove('rs-item', 'rs-in');
    item.style.removeProperty('--rs-delay');
  }

  function show(item, delay) {
    item._rsShown = true;
    item.style.setProperty('--rs-delay', delay + 'ms');
    item.classList.add('rs-in');
    clearTimeout(item._rsTimer);
    item._rsTimer = setTimeout(function () { finish(item); }, delay + DURATION_MS + 100);
  }

  // Re-arm once the item is completely off screen (never visible to the
  // user), so scrolling back to the section plays the reveal again.
  function reset(item) {
    item._rsShown = false;
    clearTimeout(item._rsTimer);
    item.classList.remove('rs-in');
    item.classList.add('rs-item');
    item.style.removeProperty('--rs-delay');
  }

  // Horizontal rails (overflow-x: auto) get overflow-y computed as auto too,
  // so a card waiting to reveal (shifted down 3rem here, 34px in the
  // sections' own reveals) makes the rail taller than itself and it starts
  // scrolling vertically inside the page. Rails only ever scroll sideways —
  // lock the vertical axis. Runs even when animations are off (cheap, and
  // the same overflow happens with any hover lift/shadow).
  function lockRail(el) {
    var style = getComputedStyle(el);
    if (!/(auto|scroll)/.test(style.overflowX) || !/(auto|scroll)/.test(style.overflowY)) return;
    var isRow = /flex/.test(style.display) && !/column/.test(style.flexDirection);
    if (isRow || el.scrollWidth > el.clientWidth) el.style.overflowY = 'hidden';
  }

  function lockRailsVertically() {
    (document.getElementById('MainContent') || document).querySelectorAll('*').forEach(lockRail);
  }

  // Run fns one after another, yielding to the browser every ~30ms so no
  // single task gets long (anything over 50ms counts toward TBT).
  function runChunked(fns, done) {
    var i = 0;
    (function step() {
      var start = performance.now();
      while (i < fns.length && performance.now() - start < 30) fns[i++]();
      if (i < fns.length) setTimeout(step, 0);
      else done();
    })();
  }

  function init() {
    var path = window.location.pathname;
    var animate =
      !(window.Shopify && window.Shopify.designMode) &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
      'IntersectionObserver' in window &&
      // No fade-in on collection pages on mobile (Orizza and Frost): the
      // product grid shows straight away. /collections/x/products/y is a
      // product page and keeps the reveal.
      !(
        window.matchMedia('(max-width: 989px)').matches &&
        /\/collections\//.test(path) &&
        !/\/products\//.test(path)
      );

    // Rail locking and the item-group scan both touch every element; done
    // as one task that blocked the main thread for 100ms+ during load, so
    // it now runs element by element in short (~30ms) tasks.
    var allItems = [];
    var steps = [];
    document.querySelectorAll('#MainContent > .shopify-section').forEach(function (section) {
      steps.push.apply(steps, sectionSteps(section, animate, allItems));
    });
    runChunked(steps, function () { setUp(animate, allItems); });
  }

  function setUp(animate, allItems) {
    // Layouts switch at breakpoints (e.g. a grid that becomes a rail on mobile)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { lockRailsVertically(); }, 200);
    });
    if (!animate || !allItems.length) return;

    // Items already on screen when this runs were painted visible at load —
    // hiding them now would make them blink out and fade back in, and would
    // push the page's LCP (usually the first product image) to the end of
    // that fade. Leave them as they are; they only animate if scrolled away
    // and back. All rects are read first, before any class is written, so
    // this costs a single layout.
    var viewH = window.innerHeight || document.documentElement.clientHeight;
    var onScreen = allItems.map(function (item) {
      var r = item.getBoundingClientRect();
      return r.top < viewH && r.bottom > 0;
    });
    allItems.forEach(function (item, i) {
      if (onScreen[i]) item._rsShown = true;
      else item.classList.add('rs-item');
    });

    var SHOW_RATIO = 0.1;

    // Items wider/taller than the screen (e.g. a long strip on mobile) can
    // never reach SHOW_RATIO of their own area, so they'd stay hidden for
    // good. Count them as visible once they fill enough of the viewport.
    function isVisibleEnough(e) {
      if (e.intersectionRatio >= SHOW_RATIO) return true;
      var root = e.rootBounds;
      if (!root || !e.isIntersecting) return false;
      var r = e.intersectionRect;
      return r.width >= root.width * 0.5 && r.height >= Math.min(e.boundingClientRect.height, root.height) * 0.5;
    }

    var showObserver = new IntersectionObserver(
      function (entries) {
        // Items that come into view together play in page order, one by one.
        entries
          .filter(function (e) { return isVisibleEnough(e) && !e.target._rsShown; })
          .map(function (e) { return e.target; })
          .sort(function (a, b) {
            return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
          })
          .forEach(function (item, index) {
            show(item, Math.min(index * STEP_MS, MAX_DELAY_MS));
          });
      },
      { threshold: [0, 0.05, 0.1, SHOW_RATIO], rootMargin: '0px 0px -8% 0px' }
    );

    // Separate observer on the real viewport (no margin) so an item is only
    // reset once it's fully off screen — never while any of it still shows.
    var resetObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting && e.target._rsShown) reset(e.target);
      });
    });

    // Let the hidden state paint first so the very first reveal animates.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        allItems.forEach(function (item) {
          showObserver.observe(item);
          resetObserver.observe(item);
        });
      });
    });
  }

  // Scanning every section for item groups touches every element on the
  // page, which was a long main-thread task right during load (TBT). Nothing
  // on screen depends on it any more (see onScreen in init), so it waits
  // for the browser to be idle — with a cap so off-screen items are still
  // set up well before anyone scrolls to them.
  function schedule() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(init, { timeout: 1500 });
    } else {
      setTimeout(init, 200);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
